import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { classInfoService } from '../services/ClassInfoService';
import ChildTabSelector from './ChildTabSelector';
import AssignmentDetailView from './AssignmentDetailView';
import './ParentAssignmentsView.css';
import './ParentDetailPages.css';

function ParentAssignmentsView({ children }) {
  const [selectedChild, setSelectedChild] = useState(null);
  const [student, setStudent] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('all');

  const handleChildSelect = (child) => {
    setSelectedChild(child);
  };

  useEffect(() => {
    if (selectedChild) {
      loadAssignmentData(selectedChild);
    }
  }, [selectedChild]);

  const loadAssignmentData = async (child) => {
    try {
      setLoading(true);
      setStudent(null);
      setClassInfo(null);
      setAssignments([]);
      setGrades([]);
      setSubmissions([]);

      // Use child data directly (iOS approach)
      const studentData = {
        id: child.id,
        studentId: child.studentId,
        classId: child.classId,
        fullName: child.fullName || `${child.firstName || ''} ${child.lastName || ''}`.trim() || 'Unknown',
        firstName: child.firstName,
        lastName: child.lastName,
      };

      setStudent(studentData);
      console.log('🔥 ASSIGNMENTS: Using child data directly:', studentData.fullName);

      // Load class info
      if (studentData.classId) {
        try {
          const classData = await classInfoService.getClassInfo(studentData.classId);
          setClassInfo(classData);
        } catch (error) {
          console.log('🔥 ASSIGNMENTS: Error loading class info:', error);
          setClassInfo({
            id: studentData.classId,
            name: 'Class ' + studentData.classId.slice(-8),
            grade: 'N/A',
            source: 'error'
          });
        }
      }

      // Load assignments from class subcollection
      const assignmentsData = [];
      const gradesData = [];
      const submissionsData = [];

      if (studentData.classId) {
        try {
          const assignmentsSubcollection = collection(db, 'classes', studentData.classId, 'assignments');
          const assignmentsSnapshot = await getDocs(assignmentsSubcollection);

          console.log('🔥 ASSIGNMENTS: Found', assignmentsSnapshot.docs.length, 'assignments');

          for (const assignmentDoc of assignmentsSnapshot.docs) {
            const assignmentData = assignmentDoc.data();
            // Filter out in-class-only assignments - parents should not see these
            if (assignmentData.isInClassOnly === true) continue;
            assignmentsData.push({ id: assignmentDoc.id, ...assignmentData });

            // Load grades for this assignment
            try {
              const gradesSubcollection = collection(db, 'classes', studentData.classId, 'assignments', assignmentDoc.id, 'grades');
              const gradesQuery = query(gradesSubcollection, where('studentId', '==', child.id));
              const gradesSnapshot = await getDocs(gradesQuery);

              gradesSnapshot.forEach(gradeDoc => {
                gradesData.push({ id: gradeDoc.id, assignmentId: assignmentDoc.id, ...gradeDoc.data() });
              });

              // Also try with studentId field
              if (gradesSnapshot.empty && child.studentId) {
                const gradesQuery2 = query(gradesSubcollection, where('studentId', '==', child.studentId));
                const gradesSnapshot2 = await getDocs(gradesQuery2);
                gradesSnapshot2.forEach(gradeDoc => {
                  gradesData.push({ id: gradeDoc.id, assignmentId: assignmentDoc.id, ...gradeDoc.data() });
                });
              }
            } catch (gradeError) {
              console.log('🔥 ASSIGNMENTS: Error loading grades for assignment', assignmentDoc.id, gradeError);
            }

            // Load submissions for this assignment
            try {
              const submissionsSubcollection = collection(db, 'classes', studentData.classId, 'assignments', assignmentDoc.id, 'assignmentSubmissions');
              const submissionsQuery = query(submissionsSubcollection, where('studentId', '==', child.id));
              const submissionsSnapshot = await getDocs(submissionsQuery);

              submissionsSnapshot.forEach(subDoc => {
                submissionsData.push({ id: subDoc.id, assignmentId: assignmentDoc.id, ...subDoc.data() });
              });

              // Also try with studentId field
              if (submissionsSnapshot.empty && child.studentId) {
                const submissionsQuery2 = query(submissionsSubcollection, where('studentId', '==', child.studentId));
                const submissionsSnapshot2 = await getDocs(submissionsQuery2);
                submissionsSnapshot2.forEach(subDoc => {
                  submissionsData.push({ id: subDoc.id, assignmentId: assignmentDoc.id, ...subDoc.data() });
                });
              }
            } catch (subError) {
              console.log('🔥 ASSIGNMENTS: Error loading submissions for assignment', assignmentDoc.id, subError);
            }
          }
        } catch (error) {
          console.log('🔥 ASSIGNMENTS: Error loading assignments:', error);
        }
      }

      console.log('🔥 ASSIGNMENTS: Loaded', assignmentsData.length, 'assignments,', gradesData.length, 'grades,', submissionsData.length, 'submissions');
      setAssignments(assignmentsData);
      setGrades(gradesData);
      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error loading assignment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeForAssignment = (assignmentId) => {
    return grades.find(grade => grade.assignmentId === assignmentId);
  };

  const getSubmissionForAssignment = (assignmentId) => {
    return submissions.find(sub => sub.assignmentId === assignmentId);
  };

  const isAssignmentCompleted = (assignmentId) => {
    return grades.some(grade => grade.assignmentId === assignmentId);
  };

  const getAssignmentStatus = (assignment) => {
    const grade = getGradeForAssignment(assignment.id);
    if (grade) {
      return { status: 'graded', grade };
    }
    const submission = getSubmissionForAssignment(assignment.id);
    if (submission) {
      if (submission.status === 'submitted') return { status: 'submitted', submission };
      if (submission.status === 'late') return { status: 'late', submission };
      if (submission.status === 'started') return { status: 'started', submission };
      if (submission.status === 'graded') return { status: 'graded', submission };
      if (submission.status === 'returned') return { status: 'returned', submission };
    }
    const dueDate = assignment.dueDate?.toDate?.() ? new Date(assignment.dueDate.toDate()) : new Date(assignment.dueDate);
    const now = new Date();
    if (dueDate < now) {
      return { status: 'overdue' };
    }
    return { status: 'pending' };
  };

  const getFilteredAssignments = () => {
    const now = new Date();
    switch (filter) {
      case 'all':
        return assignments;
      case 'upcoming':
        return assignments.filter(a => {
          const dueDate = a.dueDate?.toDate?.() ? new Date(a.dueDate.toDate()) : new Date(a.dueDate);
          return dueDate > now && !isAssignmentCompleted(a.id);
        });
      case 'completed':
        return assignments.filter(a => isAssignmentCompleted(a.id));
      case 'overdue':
        return assignments.filter(a => {
          const dueDate = a.dueDate?.toDate?.() ? new Date(a.dueDate.toDate()) : new Date(a.dueDate);
          return dueDate < now && !isAssignmentCompleted(a.id);
        });
      default:
        return assignments;
    }
  };

  const filteredAssignments = getFilteredAssignments();

  const getSummaryCounts = () => {
    const now = new Date();
    const total = assignments.length;
    const upcoming = assignments.filter(a => {
      const dueDate = a.dueDate?.toDate?.() ? new Date(a.dueDate.toDate()) : new Date(a.dueDate);
      return dueDate > now && !isAssignmentCompleted(a.id);
    }).length;
    const overdue = assignments.filter(a => {
      const dueDate = a.dueDate?.toDate?.() ? new Date(a.dueDate.toDate()) : new Date(a.dueDate);
      return dueDate < now && !isAssignmentCompleted(a.id);
    }).length;
    return { total, upcoming, overdue };
  };

  const summaryCounts = getSummaryCounts();

  if (children.length === 0) {
    return (
      <div className="empty-children">
        <div className="empty-icon">📝</div>
        <h3>No Children Linked</h3>
        <p>Add your child using their Student ID to view their assignments</p>
      </div>
    );
  }

  return (
    <div className="assignments-wrapper">
      <ChildTabSelector children={children} onChildSelect={handleChildSelect} />
      
      {selectedChild && (
        <div className="assignments-container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading assignments...</p>
            </div>
          ) : !student ? (
            <div className="empty-state">
              <p>Student information not found</p>
            </div>
          ) : (
            <>
              <div className="student-header">
                <h2>{student.fullName}</h2>
                <p>Student ID: {student.studentId}</p>
                <p>Class: {classInfo?.name || 'Not assigned'} ({classInfo?.grade || 'N/A'})</p>
              </div>

              {assignments.length > 0 && (
                <div className="assignments-summary">
                  <div className="filter-picker">
                    <button className={`filter-button ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
                    <button className={`filter-button ${filter === 'upcoming' ? 'active' : ''}`} onClick={() => setFilter('upcoming')}>Upcoming</button>
                    <button className={`filter-button ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>Completed</button>
                    <button className={`filter-button ${filter === 'overdue' ? 'active' : ''}`} onClick={() => setFilter('overdue')}>Overdue</button>
                  </div>
                  <div className="summary-badges">
                    <div className="summary-badge total">
                      <span className="badge-label">Total</span>
                      <span className="badge-count">{summaryCounts.total}</span>
                    </div>
                    <div className="summary-badge upcoming">
                      <span className="badge-label">Upcoming</span>
                      <span className="badge-count">{summaryCounts.upcoming}</span>
                    </div>
                    <div className="summary-badge overdue">
                      <span className="badge-label">Overdue</span>
                      <span className="badge-count">{summaryCounts.overdue}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="assignments-content">
                <h3>Assignments</h3>
                
                {filteredAssignments.length === 0 ? (
                  <div className="empty-state">
                    <p>No assignments available</p>
                    <p className="empty-subtitle">Assignments will appear here once teachers create them</p>
                  </div>
                ) : (
                  <div className="assignments-list">
                    {filteredAssignments
                      .sort((a, b) => {
                        const dateA = a.dueDate?.toDate?.() ? new Date(a.dueDate.toDate()) : new Date(a.dueDate);
                        const dateB = b.dueDate?.toDate?.() ? new Date(b.dueDate.toDate()) : new Date(b.dueDate);
                        return dateA - dateB;
                      })
                      .map((assignment, index) => {
                        const assignmentInfo = getAssignmentStatus(assignment);
                        const grade = assignmentInfo.grade;
                        const submission = assignmentInfo.submission;
                        
                        return (
                          <div key={index} className={`assignment-card ${assignmentInfo.status} clickable`} onClick={() => {
                            setSelectedAssignment(assignment);
                            setSelectedGrade(grade);
                            setSelectedSubmission(submission);
                          }}>
                            <div className="assignment-header">
                              <h4>{assignment.title}</h4>
                              <span className={`status-badge ${assignmentInfo.status}`}>
                                {assignmentInfo.status === 'graded' ? 'Graded' :
                                 assignmentInfo.status === 'submitted' ? 'Submitted' :
                                 assignmentInfo.status === 'late' ? 'Late' :
                                 assignmentInfo.status === 'started' ? 'In Progress' :
                                 assignmentInfo.status === 'returned' ? 'Returned' :
                                 assignmentInfo.status === 'overdue' ? 'Overdue' : 'Pending'}
                              </span>
                            </div>
                            
                            <div className="assignment-details">
                              <p><strong>Type:</strong> {assignment.assignmentType || 'N/A'}</p>
                              {(assignment.subjectName || assignment.subjectCategory) && (
                                <p><strong>Subject:</strong> {assignment.subjectName || assignment.subjectCategory}</p>
                              )}
                              <p><strong>Due Date:</strong> {
                                assignment.dueDate?.toDate?.() ? 
                                  new Date(assignment.dueDate.toDate()).toLocaleDateString() : 
                                  assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 
                                  'No due date'
                              }</p>
                              <p><strong>Max Score:</strong> {assignment.maxScore || 'N/A'}</p>
                              
                              {grade && (
                                <div className="grade-info">
                                  <p><strong>Score:</strong> {grade.score}/{grade.maxScore}</p>
                                  {grade.comments && (
                                    <p className="grade-comments"><strong>Comments:</strong> {grade.comments}</p>
                                  )}
                                </div>
                              )}
                              
                              {submission && !grade && (
                                <div className="submission-info" style={{ marginTop: '8px', padding: '8px', background: '#f0fdf4', borderRadius: '6px' }}>
                                  <p><strong>Submission Status:</strong> {submission.status}</p>
                                  {submission.submittedAt && (
                                    <p><strong>Submitted:</strong> {new Date(submission.submittedAt).toLocaleString()}</p>
                                  )}
                                  {submission.studentResponse && (
                                    <p><strong>Response:</strong> {submission.studentResponse.substring(0, 100)}{submission.studentResponse.length > 100 ? '...' : ''}</p>
                                  )}
                                </div>
                              )}
                              
                              {assignment.description && (
                                <div className="assignment-description">
                                  <p><strong>Description:</strong> {assignment.description}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {selectedAssignment && (
        <div className="detail-page">
          <button
            className="detail-back-button"
            onClick={() => {
              setSelectedAssignment(null);
              setSelectedGrade(null);
              setSelectedSubmission(null);
            }}
          >
            ◄ Back to Assignments
          </button>
          <AssignmentDetailView
            assignment={selectedAssignment}
            grade={selectedGrade}
            submission={selectedSubmission}
            student={student}
            onClose={() => {
              setSelectedAssignment(null);
              setSelectedGrade(null);
              setSelectedSubmission(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default ParentAssignmentsView;
